/**
 * PROJECT-111A T0844 — the review queue's INBOX.
 *
 * Every background kernel-fix run, newest first, with the existing per-run panel opening
 * underneath the row you pick.
 *
 * Why this page exists
 * --------------------
 * `KernelFixRunPanel` is mounted inside `MarkableOutput` and needs the `patchRunId` that
 * `/compose-version` handed back. Navigate away from that output and the run became
 * **unreachable from the UI** — its proposals still existed, nothing listed them. A run
 * whose proposals nobody applied was therefore indistinguishable from a run that never
 * happened, which is D-002's blind spot ("nobody is watching at write time") moved up one
 * level onto the REVIEW step.
 *
 * D-016 still holds: no differ, no proposal rendering, no second copy of anything. This
 * page lists runs and delegates every detail to `KernelFixRunPanel`.
 */

import { useCallback, useEffect, useState } from "react";
import { Button, Empty, Tag, Tooltip } from "antd";
import { AlertTriangle, GitBranch, Loader2, RefreshCw } from "lucide-react";

import KernelFixRunPanel from "@/components/shared/KernelFixRunPanel";
import { type PatchRunSummary, getPatchRuns } from "@/services/api/spanFeedbackApi";

const KernelRunsPage = () => {
  const [runs, setRuns] = useState<PatchRunSummary[]>([]);
  const [windowDays, setWindowDays] = useState(30);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);

  const load = useCallback(async (days: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPatchRuns({ days });
      if (res.status && res.data) {
        setRuns(res.data.runs);
        setWindowDays(res.data.windowDays);
        setTruncated(res.data.truncated);
      } else {
        setError(res.message || "Could not read the run list");
      }
    } catch (e) {
      // Surfaced, not swallowed: unlike the per-run panel this does not poll, so a
      // failure here has nothing behind it to correct the screen later.
      setError(`Could not read the run list: ${e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(30);
  }, [load]);

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-3">
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-violet-500" />
        <h1 className="text-base font-semibold flex-1">Kernel fix runs</h1>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />}
        <Button
          size="small"
          icon={<RefreshCw className="h-3 w-3" />}
          onClick={() => void load(windowDays)}
        >
          Refresh
        </Button>
      </div>

      {/* The same sentence the per-run panel leads with, because it is the thing most
          worth not forgetting: none of this is in force until someone accepts it. */}
      <div className="text-label-xs text-muted-foreground/70 leading-snug">
        Every background <span className="font-mono">62 → 73</span> run started by an
        accepted mark. <strong>Nothing here is in force</strong> — each run stops at a
        proposal, and a kernel changes only when someone applies one. Last {windowDays} days.
        {truncated && " More runs exist beyond this limit."}
      </div>

      {error && (
        <div className="flex items-start gap-1.5 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 px-2 py-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
          <span className="text-label-xs text-amber-800 dark:text-amber-400">{error}</span>
        </div>
      )}

      {!loading && !runs.length && !error && (
        <Empty
          description={
            <span className="text-xs text-muted-foreground">
              No kernel fix runs in the last {windowDays} days.
            </span>
          }
        />
      )}

      {runs.map((r) => (
        <div
          key={r.patchRunId}
          className="rounded border border-border/50 dark:border-gray-800 px-3 py-2 space-y-1.5"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs flex-1 break-all">{r.patchRunId}</span>
            {!!r.failedCallCount && (
              <Tooltip title="Some calls in this run failed — open it to see which.">
                <Tag color="warning">{r.failedCallCount} failed</Tag>
              </Tooltip>
            )}
            <Tag>{r.patchCallCount} proposal(s)</Tag>
            <Button
              size="small"
              onClick={() =>
                setOpenRun(openRun === r.patchRunId ? null : r.patchRunId)
              }
            >
              {openRun === r.patchRunId ? "Close" : "Open"}
            </Button>
          </div>

          <div className="text-label-2xs text-muted-foreground/70 flex items-center gap-2 flex-wrap">
            <span>{r.lastCallAt ? new Date(r.lastCallAt).toLocaleString() : "—"}</span>
            <span>·</span>
            <span>{r.callCount} call(s)</span>
            {r.entityId && (
              <>
                <span>·</span>
                {/* T1414 — this used to print the raw id in full
                    (`raisy_ent_03640_20260902034631`), which is a database key, not something
                    a person recognises. It is still available on hover, because on a kernel
                    surface a reader occasionally needs the exact id. */}
                <span title={r.entityId}>
                  entity <span className="font-mono">{r.entityId.slice(0, 14)}…</span>
                </span>
              </>
            )}
          </div>

          {!!r.kernelKeys.length && (
            <div className="text-label-2xs text-muted-foreground/60 font-mono break-all">
              {r.kernelKeys.join(" · ")}
            </div>
          )}

          {/* D-016 — the existing per-run panel, reused verbatim. */}
          {openRun === r.patchRunId && (
            <div className="pt-1">
              <KernelFixRunPanel
                patchRunId={r.patchRunId}
                entityId={r.entityId ?? undefined}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

KernelRunsPage.path = "/kernel-runs";

export default KernelRunsPage;
